import React, { useEffect, useMemo, useState } from 'react';
import Loading from '../../../../Components/Loading';
import {
	LaboratoryRequisitionTemplate,
	useLaboratoryRequestFormQuery,
} from '../../../../graphql/graphql';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { LaboratoryRequestItem } from '../../Interfaces/LaboratoryRequestItem.intefrace';
import LaboratoryRequestCreateFormItem from './LaboratoryRequestCreateFormItem';
import styles from './LaboratoryRequestCreateForm.module.scss';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import LaboratoryRequestCreateFormSamples from './LaboratoryRequestCreateFormSamples';
import { LaboratoryRequestSample } from '../../Interfaces/LaboratoryRequestSample.interface';
import useFilters from '../../../Base/Hooks/useFilters';
import Text from '../../../../Components/Base/Text/Text';
import useCustomInputs from '../../../Base/Hooks/useCustomInputs';
import useLaboratoryRequestFormResolver, {
	ResolveHelpItem,
} from '../../Hooks/useLaboratoryRequestFormResolver';
import { isEqual } from 'lodash';
import useNotification from '../../../../Hooks/useNotification.hook';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import LaboratoryRequestTemplateSelectionModal from '../LaboratoryRequestTemplate/LaboratoryRequestTemplateSelection/LaboratoryRequestTemplateSelectionModal';
import { useAppContext } from '../../../../Services/AppContext';

interface Props {
	formId: string;
	onSuccess: (values: LaboratoryRequestFormDataScheme[]) => void;
	initialData?: LaboratoryRequestFormDataScheme[];
	defaultValues?: LaboratoryRequestFormDataScheme[];
	templatesEnabled?: boolean;
	workplaceId: string;
}

export interface LaboratoryRequestCreateFormScheme {
	children: LaboratoryRequestItem[];
	samples?: LaboratoryRequestSample[];
	settings?: object;
}

export interface LaboratoryRequestFormDataScheme {
	key: string;
	wizardItemId: string;
	value: string;
	note?: string;
}

const mapFormDataToDefaultValues = (
	values?: LaboratoryRequestFormDataScheme[],
): Record<string, string | boolean> => {
	if (!values?.length) {
		return {};
	}
	return values.reduce<Record<string, string | boolean>>((acc, item) => {
		acc[item.key] = item.value === 'true' ? true : item.value;
		return acc;
	}, {});
};

const LaboratoryRequestCreateForm: React.FC<Props> = ({
	formId,
	onSuccess,
	initialData,
	defaultValues,
	templatesEnabled,
	workplaceId,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [formScheme, setFormScheme] = useState<
		LaboratoryRequestCreateFormScheme | undefined
	>(undefined);
	const [activeSamples, setActiveSamples] = useState<string[]>([]);
	const [activeItems, setActiveItems] = useState<LaboratoryRequestItem[]>([]);
	const {
		isOpen: isOpenTemplateSelection,
		onClose: onCloseTemplateSelection,
		onOpen: onOpenTemplateSelection,
	} = useDisclose(false);
	const { userData } = useAppContext();

	const { formResolveHelperData } =
		useLaboratoryRequestFormResolver(formScheme);

	const { checkEmptyString } = useFilters();
	const { reconstructGroupCheckboxKey, checkIsGroupCheckbox } =
		useCustomInputs();

	const { showNotificationAlert } = useNotification();

	const { data, isLoading: dataIsLoading } = useLaboratoryRequestFormQuery({
		where: { id: formId },
	});

	const handleItemSelect = (item: LaboratoryRequestItem) => {
		setActiveItems(prev => {
			if (prev.some(i => i.value === item.value)) {
				return [...prev.filter(i => i.value !== item.value)];
			} else {
				return [...prev, item];
			}
		});
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);
		const result: LaboratoryRequestFormDataScheme[] = [];

		const truthyValues = Object.values(values).filter(Boolean);
		if (!truthyValues.length) {
			showNotificationAlert({
				content: 'component.laboratoryRequestCreateForm.noRequestsFilled',
			});
			setIsLoading(false);
			return;
		}

		const pushToResult = ({
			helperItem,
			key,
			value,
		}: {
			helperItem: ResolveHelpItem;
			key: string;
			value: string;
		}) => {
			result.push({
				wizardItemId: helperItem.wizardItemId,
				note: helperItem.name,
				key,
				value,
			});
		};

		const findHelperItemByKey = (key: any): ResolveHelpItem | undefined => {
			return formResolveHelperData.find(h => isEqual(h.key, key));
		};

		for (const resultKey in values) {
			const value = values[resultKey];
			if (typeof value === 'boolean' && value) {
				//RESOLVING BOOLEAN VALUES, NEED TO FILTER ONLY TRUE VALUES AND ADD THEM INTRO FINAL RESULT AS STRING
				//IF IS GROUP CHECKBOX WE NEED TO FIND HELPER VALUE USING THE SAME KEY AS API ORIGINALLY SEND, SO WE NEED TO CALL RECONSTRUCT FUNCTION TO ACHIEVE THAT
				const helperItem = checkIsGroupCheckbox(resultKey)
					? findHelperItemByKey(reconstructGroupCheckboxKey(resultKey))
					: findHelperItemByKey(resultKey);
				if (helperItem) {
					pushToResult({
						helperItem,
						key: resultKey,
						value: 'true',
					});
				}
			} else if (typeof value === 'string' && checkEmptyString(value)) {
				// RESOLVING TYPE STRING, JUST REMOVING EMPTY FIELDS AND PUSHING REAL ENTERED VALUE
				const helperItem = findHelperItemByKey(resultKey);
				if (helperItem) {
					pushToResult({
						helperItem,
						key: resultKey,
						value,
					});
				}
			}
		}

		setIsLoading(false);
		onSuccess(result);
	};

	const reconstructedDefaultValues = useMemo(
		() => mapFormDataToDefaultValues(defaultValues),
		[defaultValues],
	);

	const formMethods = useForm({ defaultValues: reconstructedDefaultValues });

	useEffect(() => {
		setIsLoading(true);

		if (data?.laboratoryRequisitionForm?.form) {
			setFormScheme(
				data.laboratoryRequisitionForm
					.form as LaboratoryRequestCreateFormScheme,
			);
		}
		setIsLoading(false);
	}, [data]);

	useEffect(() => {
		if (!formScheme?.children || !initialData?.length) return;

		const defaultValues = Object.fromEntries(
			initialData.map(item => [
				item.key,
				item.value === 'true' ? true : item.value,
			]),
		);
		formMethods.reset(defaultValues);
	}, [formScheme, initialData]);

	useEffect(() => {
		const tempSamples: string[] = [];
		activeItems.forEach(item => {
			item.samples?.forEach(sample => {
				if (!tempSamples.includes(sample)) {
					tempSamples.push(sample);
				}
			});
		});
		setActiveSamples(tempSamples);
	}, [activeItems]);

	const handleFillFromTemplate = (template: LaboratoryRequisitionTemplate) => {
		formMethods.reset(mapFormDataToDefaultValues(template.data.values));
		onCloseTemplateSelection();
	};

	if (isLoading || dataIsLoading) {
		return <Loading />;
	}

	if (!formScheme?.children?.length) {
		return <Text tx='component.laboratoryRequestCreateForm.schemeNotFound' />;
	}

	return (
		<>
			<div className={styles.wrapper}>
				{templatesEnabled && (
					<div className={styles.controls}>
						<LinkButton
							title='component.laboratoryRequestCreateForm.loadFromTemplate'
							theme={Theme.BLUE}
							onClick={onOpenTemplateSelection}
						/>
					</div>
				)}
				<div className={styles.formWrapper}>
					<FormWrapper formMethods={formMethods}>
						{formScheme?.children.map((item, key) => (
							<LaboratoryRequestCreateFormItem
								item={item}
								key={key}
								onItemSelect={handleItemSelect}
							/>
						))}
						<div className='mt-4'>
							<SubmitButton
								onSubmit={handleSubmit}
								title='base.shared.continue'
							/>
						</div>
					</FormWrapper>
				</div>
				{(formScheme?.samples?.length || 0) > 0 && (
					<LaboratoryRequestCreateFormSamples
						activeSamples={activeSamples}
						items={formScheme.samples || []}
					/>
				)}
			</div>
			{isOpenTemplateSelection && (
				<LaboratoryRequestTemplateSelectionModal
					workplaceId={workplaceId}
					doctorId={userData.id}
					formId={formId}
					onSelect={handleFillFromTemplate}
					isOpen
					onClose={onCloseTemplateSelection}
				/>
			)}
		</>
	);
};

export default LaboratoryRequestCreateForm;
